import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { User } from '@/types/user';
import { Paginated } from '@/types/pagination';
import { ProfileTaste } from '@/types/taste';
import * as usersApi from '../endpoints/users';
import { getUserTaste } from '../endpoints/taste';
import { SearchUsersParams } from '../endpoints/users';

export const PROFILE_QUERY_KEYS = {
  all: ['profile'] as const,
  user: (id: string) => [...PROFILE_QUERY_KEYS.all, 'user', id] as const,
  /**
   * Current-user profile key.
   *
   * With a userId it is the exact cache entry for that account -- queries,
   * reads (`getQueryData`), and writes (`setQueryData`) must pass it, so one
   * account's cached (and persisted) profile can never satisfy another's read.
   *
   * Without an argument it is the `['profile','current']` family prefix, used
   * only to invalidate every current-user entry at once regardless of account.
   */
  currentUser: (id?: string) =>
    (id === undefined
      ? [...PROFILE_QUERY_KEYS.all, 'current']
      : [...PROFILE_QUERY_KEYS.all, 'current', id]) as readonly string[],
  taste: (id: string) => [...PROFILE_QUERY_KEYS.all, 'taste', id] as const,
} as const;

/**
 * Query options for a user's aggregate taste distribution.
 * Keyed on the profile user's id; enabled whenever an id is present.
 */
export const userTasteQueryOptions = (userId: string) =>
  queryOptions({
    queryKey: PROFILE_QUERY_KEYS.taste(userId),
    queryFn: async (): Promise<ProfileTaste> => getUserTaste(userId),
    enabled: !!userId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  });

// Re-export ProfileData type for backward compatibility
export interface ProfileData {
  user: User;
}

/**
 * Domain-oriented query options for user operations
 * Provides reusable queryOptions that can be used in hooks or prefetching
 */

/**
 * Query options for current user profile
 * Requires userId parameter for the API call (backend requires ID in path)
 */
export const currentUserQueryOptions = (userId: string) =>
  queryOptions({
    // Scope by userId: the result is persisted to disk, so a shared key would
    // let account B render account A's profile after a switch (until refetch).
    queryKey: PROFILE_QUERY_KEYS.currentUser(userId),
    queryFn: async (): Promise<ProfileData> => {
      return usersApi.getCurrentUser(userId);
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
    meta: { persist: true },
  });

/**
 * Query options for user profile by ID
 */
export const userQueryOptions = (userId: string) =>
  queryOptions({
    queryKey: PROFILE_QUERY_KEYS.user(userId),
    queryFn: async (): Promise<ProfileData> => {
      return usersApi.getUserById(userId);
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 15, // 15 minutes
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
  });

/**
 * Query options for user profile by username
 */
export const userByUsernameQueryOptions = (username: string) =>
  queryOptions({
    queryKey: [...PROFILE_QUERY_KEYS.all, 'username', username.toLowerCase().replace(/^@+/, '')] as const,
    queryFn: async (): Promise<ProfileData> => {
      return usersApi.getUserByUsername(username);
    },
    enabled: !!username,
    staleTime: 1000 * 60 * 15, // 15 minutes
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
  });

/**
 * Query keys for user search
 */
export const USERS_SEARCH_QUERY_KEYS = {
  all: ['users', 'search'] as const,
  search: (params: SearchUsersParams) => [...USERS_SEARCH_QUERY_KEYS.all, params] as const,
} as const;

/**
 * Query options for searching users with text query
 *
 * @param params - Search parameters including query text and pagination
 * @returns Query options for the users search
 *
 * @example
 * ```typescript
 * // Search for users matching "john"
 * const options = usersSearchQueryOptions({
 *   query: 'john',
 *   per_page: 20
 * });
 * ```
 */
export const usersSearchQueryOptions = (params: SearchUsersParams = {}) =>
  queryOptions({
    queryKey: USERS_SEARCH_QUERY_KEYS.search(params),
    queryFn: async (): Promise<Paginated<User>> => {
      return usersApi.searchUsers(params);
    },
    enabled: (params.query?.trim().length ?? 0) >= 2, // Only search if query is at least 2 characters
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 15, // 15 minutes
    meta: {
      persist: false, // No persisted cache: auth-required data
    },
  });

export const usersSearchInfiniteQueryOptions = (params: Omit<SearchUsersParams, 'page'>) =>
  infiniteQueryOptions({
    queryKey: [...USERS_SEARCH_QUERY_KEYS.all, params, 'infinite'] as const,
    queryFn: async ({ pageParam }): Promise<Paginated<User>> => {
      return usersApi.searchUsers({ ...params, page: pageParam as number });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
    enabled: (params.query?.trim().length ?? 0) >= 2,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 15,
    meta: { persist: false },
  });
